// src/features/breakout-alerts/api.ts
//
// Typed client for the pre-computed momentum-breakout endpoint
// (GET /api/v1/breakout-alerts/{symbol}) that backs <BreakoutAlertWidget />.
//
// The TypeScript contract below mirrors the backend Pydantic model in
// backend/app/domain/breakout_alerts/schemas.py (BreakoutAlertDTO) — keep
// the two in sync when the API changes.
//
// AUTH: we deliberately go through the shared axios `apiClient` (not a raw
// fetch) so the request automatically carries the Forge Ultra JWT
// (Authorization: Bearer, sourced from the `forge_token` localStorage key
// by the request interceptor) AND the `forge_session` cookie, and so a 401
// flows through the central silent-refresh / bounce-to-WP handling in
// api/client.ts — exactly like every other authenticated call in the SPA.
// The apiClient already bakes the `/api/v1` prefix into its baseURL, so we
// request the bare `/breakout-alerts/{symbol}` path here.

import axios from 'axios';

import { apiClient } from '../../api/client';

export type AlertSignal =
    | 'STRONG_BUY'
    | 'BUY_WATCH'
    | 'HOLD_CONSOLIDATING'
    | 'SELL_WATCH'
    | 'STRONG_SELL'
    | 'INSUFFICIENT_DATA';

export interface BreakoutAlertDTO {
    symbol: string;
    asset_class: string;
    signal: AlertSignal;
    close_price: number;
    prev_20d_high?: number | null;
    prev_20d_low?: number | null;
    sma_50?: number | null;
    sma_200?: number | null;
    volume_surge_multiplier?: number | null;

    // --- NEW INSTITUTIONAL METRICS ---
    atr_20?: number | null;
    close_position?: number | null;

    updated_at: string;
}

export interface BreakoutInsightDTO extends BreakoutAlertDTO {
    name?: string | null;
    exchange?: string | null;
    currency?: string | null;
    region?: 'US' | 'EU' | string | null;
    market_cap_usd?: number | null;
}

/**
 * Daily momentum leaderboard payload from GET /breakout-alerts/insights/top.
 * Mirrors backend `BreakoutInsightsResponse` — two pre-ranked boards sorted
 * by `volume_surge_multiplier` plus the server-side scan time (perf metric).
 */
export interface BreakoutInsightsResponse {
    top_buys: BreakoutInsightDTO[];
    top_sells: BreakoutInsightDTO[];
    execution_time_ms: number;
}

/**
 * Fetch the pre-computed breakout alert for a symbol.
 *
 * Returns `null` (rather than throwing) when the asset has no alert — a 404
 * simply means the symbol lacks 200 days of history (e.g. a recent IPO) or
 * was not in the equity/ETF universe processed by the sync worker. Any
 * other error also degrades gracefully to `null` so the widget can
 * silently unmount instead of breaking the surrounding Info Page. A 401 is
 * still handled centrally by the apiClient interceptor before it reaches
 * the catch here (silent refresh / bounce-to-WP).
 */
export const fetchBreakoutAlert = async (
    symbol: string,
): Promise<BreakoutAlertDTO | null> => {
    if (!symbol) return null;

    try {
        const { data } = await apiClient.get<BreakoutAlertDTO>(
            `/breakout-alerts/${encodeURIComponent(symbol)}`,
        );
        return data;
    } catch (error) {
        // 404 = no alert exists for this symbol; not an error worth logging.
        if (axios.isAxiosError(error) && error.response?.status === 404) {
            return null;
        }
        // eslint-disable-next-line no-console
        console.error(`Failed to fetch breakout alert for ${symbol}:`, error);
        return null; // Fail gracefully so the page doesn't break.
    }
};

/**
 * Fetch the daily momentum leaderboards (top buys + top sells).
 *
 * Served entirely off the pre-computed ``breakout_alerts`` composite index
 * (no live FMP/Numba work on the request path). ``limit`` is clamped by the
 * backend to 1..50. Returns ``null`` on failure so the dashboard can render
 * an empty/error state instead of crashing. A 401 is handled centrally by
 * the apiClient interceptor (silent refresh / bounce-to-WP) before it lands
 * in the catch here.
 */
export const fetchBreakoutInsights = async (
    limit = 10,
): Promise<BreakoutInsightsResponse | null> => {
    try {
        const { data } = await apiClient.get<BreakoutInsightsResponse>(
            '/breakout-alerts/insights/top',
            { params: { limit } },
        );
        return data;
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch breakout insights:', error);
        return null; // Fail gracefully so the dashboard doesn't break.
    }
};
